import React, { createContext, ReactNode, useContext, useState } from "react";

// Define user roles
export type UserRole = "guest" | "member" | "staff" | "admin";

// Define user interface
export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  phoneNumber?: string;
  bloodType?: string;
  address?: string;
  dateOfBirth?: string;
  emergencyContact?: {
    name: string;
    phoneNumber: string;
    medicalConditions?: string | string[];
  };
}

// Define auth context interface
interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  requestPasswordReset: (email: string) => Promise<boolean>;
  resetPassword: (token: string, newPassword: string) => Promise<boolean>;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Create auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user data for demonstration
export const mockUsers: User[] = [
  { id: "1", name: "John Member", email: "demo@member.com", role: "member", phoneNumber: "1234567890", bloodType: "O+", address: "123 Main St", dateOfBirth: "1990-01-01", emergencyContact: { name: "Jane Member", phoneNumber: "1234567890", medicalConditions: "" } },
  { id: "2", name: "Sarah Staff", email: "demo@staff.com", role: "staff", phoneNumber: "0987654321", bloodType: "A+", address: "456 Elm St", dateOfBirth: "1985-05-15", emergencyContact: { name: "John Staff", phoneNumber: "0987654321", medicalConditions: [] } },
  { id: "3", name: "Admin User", email: "demo@admin.com", role: "admin", phoneNumber: "5555555555", bloodType: "B+", address: "789 Oak St", dateOfBirth: "1980-10-10", emergencyContact: { name: "Jane Admin", phoneNumber: "5555555555", medicalConditions: [] } },
];


// Auth provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Check if there's a stored user in localStorage on initial load
  React.useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Find user by email (in a real app, this would be an API call)
      const foundUser = mockUsers.find((u) => u.email === email);

      if (foundUser && password === "demo") {
        // Simple password check for demo
        setUser(foundUser);
        localStorage.setItem("user", JSON.stringify(foundUser));
        setIsLoading(false);
        return true;
      } else {
        setError("Invalid email or password");
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      setError("An error occurred during login");
      setIsLoading(false);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  // Password reset request function
  const requestPasswordReset = async (email: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Find user by email (in a real app, this would be an API call)
      const foundUser = mockUsers.find((u) => u.email === email);

      if (foundUser) {
        // In a real app, this would send an email with a reset link
        console.log("Password reset requested for:", email);
        setIsLoading(false);
        return true;
      } else {
        setError("No account found with that email address");
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      setError("An error occurred while processing your request");
      setIsLoading(false);
      return false;
    }
  };

  // Reset password function
  const resetPassword = async (token: string, newPassword: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // In a real app, verify token and update password in database
      if (token && newPassword.length >= 6) {
        console.log("Password reset successful with token:", token);
        setIsLoading(false);
        return true;
      } else {
        setError("Invalid token or password must be at least 6 characters");
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      setError("An error occurred while resetting your password");
      setIsLoading(false);
      return false;
    }
  };

  // Auth context value
  const value = {
    user,
    login,
    logout,
    requestPasswordReset,
    resetPassword,
    isAuthenticated: !!user,
    isLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Role-based route protection component
interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles: UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    window.location.href = "/login";
    return null;
  }

  if (user && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role if not authorized
    switch (user.role) {
      case "member":
        window.location.href = "/member-dashboard";
        break;
      case "staff":
        window.location.href = "/staff-dashboard";
        break;
      case "admin":
        window.location.href = "/admin";
        break;
      default:
        window.location.href = "/";
        break;
    }
    return null;
  }

  return <>{children}</>;
};

// Helper function to get dashboard route based on user role
export const getDashboardByRole = (role: UserRole): string => {
  switch (role) {
    case "member":
      return "/member-dashboard";
    case "staff":
      return "/staff-dashboard";
    case "admin":
      return "/admin";
    default:
      return "/";
  }
};